import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  InputAdornment,
  TextField,
  Button,
} from "@mui/material";
import { ReactNode } from "react";
import SearchOutlined from "@mui/icons-material/SearchOutlined";
import CloseOutlined from "@mui/icons-material/CloseOutlined";
import { MeetingTypeInfoSelect } from "./MeetingTypeInfoSelect";

export interface FilterTypes {
  meetingType?: string;
  search?: string;
  status?: string;
  hostId?: string;
  roomKey?: string;
  partnerId?: string;
}

export const defaultFilters: FilterTypes = {
  meetingType: "All",
  search: "",
  status: "All",
  hostId: "All",
  roomKey: "All",
  partnerId: "All",
};

export interface HostWithId {
  id: string;
  name: string;
}

export interface PartnerOption {
  id: string;
  name: string;
}

interface FilterProps {
  filters: FilterTypes;
  hosts: HostWithId[];
  /** Distinct continuous room keys present in the loaded meetings. */
  roomKeys: string[];
  /** Partners available for the Partner filter (empty hides the select). */
  partners: PartnerOption[];
  onMeetingTypeChange: (event: string) => void;
  onStatusChange: (event: SelectChangeEvent<string>, child: ReactNode) => void;
  onSearchChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onHostChange: (event: SelectChangeEvent<string>, child: ReactNode) => void;
  onRoomKeyChange: (event: SelectChangeEvent<string>, child: ReactNode) => void;
  onPartnerChange: (event: SelectChangeEvent<string>, child: ReactNode) => void;
  hasFilters: boolean;
  onClearFilters: () => void;
}

export const Filters: React.FC<FilterProps> = ({
  filters,
  hosts,
  roomKeys,
  partners,
  onMeetingTypeChange,
  onStatusChange,
  onSearchChange,
  onHostChange,
  onRoomKeyChange,
  onPartnerChange,
  hasFilters,
  onClearFilters,
}) => {
  const { meetingType, search, status, hostId, roomKey, partnerId } =
    filters || {};
  return (
    // One column on mobile; 2/3 columns on wider screens so the selects don't
    // each burn a whole row. The search field always spans the full width.
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "1fr",
          sm: "repeat(2, 1fr)",
          md: "repeat(3, 1fr)",
        },
        gap: 1.5,
        margin: "0 10px",
      }}
    >
      <TextField
        key="search"
        value={search}
        onChange={onSearchChange}
        placeholder="Search meetings"
        size="small"
        fullWidth
        sx={{ gridColumn: "1 / -1" }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <SearchOutlined />
            </InputAdornment>
          ),
        }}
      />
      <FormControl size="small" fullWidth key="status">
        <InputLabel>Status</InputLabel>
        <Select value={status} onChange={onStatusChange} label="Status">
          <MenuItem value="All" key="all">
            All
          </MenuItem>
          <MenuItem value="Missing data" key="missingData">
            Missing data
          </MenuItem>
          <MenuItem value="NotVisible" key="notVisible">
            Not visible
          </MenuItem>
        </Select>
      </FormControl>
      <MeetingTypeInfoSelect
        currentMeetingTypeId={meetingType ?? "All"}
        onMeetingTypeChange={onMeetingTypeChange}
        includeAllOption={true}
        formControlProps={{
          size: "small",
          fullWidth: true,
        }}
        selectProps={{
          label: "Meeting Type",
        }}
      />
      <FormControl size="small" fullWidth key="host">
        <InputLabel>Host</InputLabel>
        <Select value={hostId ?? "All"} onChange={onHostChange} label="Host">
          <MenuItem value="All" key="all">
            All
          </MenuItem>
          {hosts.map((h) => (
            <MenuItem value={h.id} key={h.id}>
              {h.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {partners.length > 0 && (
        <FormControl size="small" fullWidth key="partner">
          <InputLabel>Partner</InputLabel>
          <Select
            value={partnerId ?? "All"}
            onChange={onPartnerChange}
            label="Partner"
          >
            <MenuItem value="All" key="all">
              All
            </MenuItem>
            {partners.map((p) => (
              <MenuItem value={p.id} key={p.id}>
                {p.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      {roomKeys.length > 0 && (
        <FormControl size="small" fullWidth key="roomKey">
          <InputLabel>Room</InputLabel>
          <Select
            value={roomKey ?? "All"}
            onChange={onRoomKeyChange}
            label="Room"
          >
            <MenuItem value="All" key="all">
              All
            </MenuItem>
            {roomKeys.map((key) => (
              <MenuItem value={key} key={key}>
                {key}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      {hasFilters && (
        <Button
          onClick={onClearFilters}
          startIcon={<CloseOutlined />}
          sx={{ gridColumn: "1 / -1", justifySelf: "start" }}
        >
          Clear all filters
        </Button>
      )}
    </Box>
  );
};
